import type { ReactElement, FC } from 'react';
import React, { Fragment } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css } from '@compiled/react';

import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { ErrorDisplay } from '@confluence/error-boundary';
import { ViewportObserver } from '@confluence/viewport-observer';

import type { HomeViewFilters } from './homeViewsQueryHelpers';
import { LoadingState } from './homeViewsQueryHelpers';
import { NoResults } from './NoResults';
import type { EmptyImageSources } from './Empty';
import { Empty } from './Empty';
import { Error } from './Error';
import type { RecentFilter } from './Recent/useRecentPreferences';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const LoadingMore = styled.div`
	margin-top: ${token('space.400', '32px')};
	/* Accounts for jumping due to spinner changing heights */
	height: 72px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const loadingMoreStyles = css({
	marginTop: token('space.400', '32px'),
	// Accounts for jumping due to spinner changing heights
	height: '72px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const LoadingOverlay = styled.div`
	pointer-events: none;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	display: flex;
	-webkit-box-align: center;
	align-items: center;
	-webkit-box-pack: center;
	justify-content: center;
	opacity: 0.5;
`;

const loadingOverlayStyles = css({
	pointerEvents: 'none',
	position: 'absolute',
	top: '0',
	right: '0',
	bottom: '0',
	left: '0',
	display: 'flex',
	'-webkit-box-align': 'center',
	alignItems: 'center',
	'-webkit-box-pack': 'center',
	justifyContent: 'center',
	opacity: '0.5',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
export const OverlaySpinnerContainer = styled.div`
	position: absolute;
	top: 200px;
	left: 50%;
`;

const overlaySpinnerContainerStyles = css({
	position: 'absolute',
	top: '200px',
	left: '50%',
});

type LoadingAndPaginationProps = {
	pageCardDataLength: number;
	loading: LoadingState;
	error?: ApolloError;
	hasNextPage: boolean;
	loadMore: () => void;
	noResultsMessage: MessageDescriptor;
	noResultsSubtext?: MessageDescriptor;
	emptyMessageHeader: MessageDescriptor;
	emptyMessageBody: MessageDescriptor;
	emptyMessageImage?: EmptyImageSources;
	emptyMessageCallToAction?: ReactElement;
	filterText: string;
	source: string;
	filter: HomeViewFilters | RecentFilter;
};

export const LoadingAndPagination: FC<LoadingAndPaginationProps> = ({
	pageCardDataLength,
	loading,
	error,
	hasNextPage,
	loadMore,
	noResultsMessage,
	noResultsSubtext,
	emptyMessageHeader,
	emptyMessageBody,
	emptyMessageImage,
	emptyMessageCallToAction,
	filterText,
	source,
	filter,
}) => {
	return (
		<Fragment>
			<div aria-live="assertive" aria-atomic="true">
				{/*empty states*/}
				{loading === LoadingState.done && pageCardDataLength === 0 && !error ? (
					filterText !== '' ? (
						<NoResults
							message={noResultsMessage}
							subMessage={noResultsSubtext}
							filterText={filterText}
							source={source}
							filter={filter}
						/>
					) : (
						<Empty
							header={emptyMessageHeader}
							body={emptyMessageBody}
							image={emptyMessageImage}
							callToAction={emptyMessageCallToAction}
						/>
					)
				) : null}

				{/*error states*/}
				{error ? (
					<ErrorDisplay error={error}>
						<Error />
					</ErrorDisplay>
				) : null}
			</div>

			{/* loading states */}
			{loading === LoadingState.initial && (
				<Fragment>
					{fg('confluence_frontend_home_compiled_migration') ? (
						<div css={loadingOverlayStyles} />
					) : (
						<LoadingOverlay />
					)}
					{fg('confluence_frontend_home_compiled_migration') ? (
						<div css={overlaySpinnerContainerStyles}>
							<Spinner size="large" testId="view-loading" />
						</div>
					) : (
						<OverlaySpinnerContainer>
							<Spinner size="large" testId="view-loading" />
						</OverlaySpinnerContainer>
					)}
				</Fragment>
			)}

			{hasNextPage && pageCardDataLength !== 0 && (
				<Fragment>
					<ViewportObserver onViewportEnter={loadMore}>
						<div
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ position: 'absolute', bottom: '0', height: '300px' }}
						/>
					</ViewportObserver>
					{fg('confluence_frontend_home_compiled_migration') ? (
						<div css={loadingMoreStyles}>
							<Spinner size="large" testId="page-loading" />
						</div>
					) : (
						<LoadingMore>
							<Spinner size="large" testId="page-loading" />
						</LoadingMore>
					)}
				</Fragment>
			)}
		</Fragment>
	);
};
